
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { supabase } from '../lib/api';
import { useUser } from '../context/UserContext';
// Tag list lives in src/lib/announcementTags.js so AdminDashboard
// (the publisher) and this component (the reader) can't drift.
import { annTagColor as tagColor } from '../lib/announcementTags';

const B = {
  black: '#1e1e23', surface: '#26262d', surfaceLight: '#2e2e36',
  border: '#1e1a2e', borderLight: '#2e2850',
  purple: '#7B5CC8', purpleDim: '#4a3880', purpleDeep: '#1a1030',
  gold: '#C9A84C', goldDim: '#8a6e2a',
  textPrimary: '#ffffff', textSecondary: '#8888aa', textMuted: '#444466',
  green: '#1D9E75', greenDeep: '#0a1f15', greenDim: '#0f6e56',
};

const XIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <path d="M8 8L20 20M20 20L32 32M20 20L32 8M20 20L8 32" stroke={B.purple} strokeWidth="4.5" strokeLinecap="round"/>
    <circle cx="20" cy="20" r="18" stroke={B.purpleDim} strokeWidth="0.5"/>
  </svg>
);

export default function HRPortal() {
  const { employee } = useUser();
  const { oktaAuth } = useOktaAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [activeCycle, setActiveCycle] = useState(null);
  const [myReview, setMyReview] = useState(null);

  useEffect(() => {
    fetchAnnouncements();
    fetchActiveCycle();
  }, []);

  useEffect(() => {
    if (activeCycle && employee) fetchMyReview();
  }, [activeCycle, employee]);

  const fetchAnnouncements = async () => {
    const { data } = await supabase
      .from('announcements')
      .select('*')
      .order('pinned', { ascending: false })
      .order('created_at', { ascending: false });
    if (data) setAnnouncements(data);
  };

  const fetchActiveCycle = async () => {
    const { data } = await supabase
      .from('review_cycles')
      .select('*')
      .eq('is_open', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();
    if (data) setActiveCycle(data);
  };

  const fetchMyReview = async () => {
    const table = activeCycle.type === 'Annual' ? 'annual_reviews' : 'midyear_reviews';
    const { data } = await supabase
      .from(table)
      .select('*')
      .eq('employee_id', employee.id)
      .eq('cycle_id', activeCycle.id)
      .single();
    setMyReview(data);
  };

  const reviewStatus = () => {
    if (!myReview) return { label: activeCycle ? `${activeCycle.label} Open` : 'No active cycle', color: B.gold, bg: '#0f0d05', border: B.goldDim };
    if (myReview.manager_status === 'completed') return { label: 'Feedback Ready', color: B.green, bg: B.greenDeep, border: B.greenDim };
    if (myReview.emp_status === 'submitted') return { label: 'Awaiting Manager', color: B.purple, bg: B.purpleDeep, border: B.purpleDim };
    if (myReview.emp_status === 'in_progress') return { label: 'In Progress', color: B.gold, bg: '#0f0d05', border: B.goldDim };
    return { label: activeCycle ? `${activeCycle.label} Open` : 'No active cycle', color: B.gold, bg: '#0f0d05', border: B.goldDim };
  };

  const reviewLink = () => {
    if (!activeCycle) return '/reviews/history';
    return activeCycle.type === 'Annual' ? '/reviews/annual' : '/reviews/midyear';
  };

  const st = reviewStatus();

  return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'var(--font-sans)' }}>
      {/* Nav */}
      <div style={{ background: B.black, borderBottom: `0.5px solid ${B.border}`, padding: '12px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => navigate('/')}
            aria-label="Go to home"
            style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
            <img src="/logo-full.png" alt="xLight" style={{ height: 22, width: 'auto', display: 'block' }} />
            <span style={{ fontSize: 13, color: B.textSecondary, letterSpacing: '0.06em' }}>myhr</span>
          </button>
          {['Manager', 'Admin'].includes(employee?.role) && (
            <>
              <span style={{ width: 1, height: 16, background: B.border, margin: '0 4px' }} />
              {/* Admins can be set as review_manager_email, so they need the manager
                  queues just like Managers. Mid-Year is always shown — the page
                  handles the no-open-cycle case gracefully. */}
              <button onClick={() => navigate('/manager/annual')} style={{ fontSize: 11, color: B.textSecondary, background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.05em' }}>MANAGER PORTAL</button>
              <button onClick={() => navigate('/manager/midyear')} style={{ fontSize: 11, color: B.textSecondary, background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.05em' }}>MID-YEAR</button>
              {employee.role === 'Admin' && <button onClick={() => navigate('/admin')} style={{ fontSize: 11, color: B.purple, background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.05em' }}>ADMIN</button>}
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 12, color: B.textSecondary }}>myhr.xlight.com</span>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: B.purpleDeep, border: `1px solid ${B.purpleDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: B.purple, cursor: 'pointer' }}
            onClick={() => oktaAuth.signOut()}>
            {employee?.name?.split(' ').map(n => n[0]).join('') || 'ME'}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1.5rem' }}>
        {/* Welcome banner */}
        <div style={{ background: B.purpleDeep, border: `0.5px solid ${B.purpleDim}`, borderRadius: 12, padding: '1.5rem 2rem', marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <p style={{ fontSize: 11, letterSpacing: '0.1em', color: B.textSecondary, textTransform: 'uppercase', margin: '0 0 6px' }}>Welcome back</p>
            <h1 style={{ fontSize: 22, fontWeight: 400, color: B.textPrimary, margin: '0 0 4px' }}>{employee?.name}</h1>
            <p style={{ fontSize: 13, color: B.textSecondary, margin: 0 }}>{employee?.role === 'Employee' ? '' : employee?.role + ' · '}{employee?.department}</p>
          </div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {[{ label: 'My manager', val: employee?.manager_name || '—' }, { label: 'Department', val: employee?.department }, { label: 'Current cycle', val: activeCycle?.label || 'None open' }].map(({ label, val }) => (
              <div key={label} style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 10, letterSpacing: '0.07em', color: B.textSecondary, margin: '0 0 3px', textTransform: 'uppercase' }}>{label}</p>
                <p style={{ fontSize: 13, color: B.textPrimary, margin: 0 }}>{val}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Portal cards */}
        <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, marginBottom: 14, textTransform: 'uppercase' }}>Your portal</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 360px))', gap: 16, marginBottom: 28 }}>
          {/* My Reviews card — not a single click target because it has
              two distinct actions (start/view current cycle, view history),
              each with its own button below. The card body itself isn't
              interactive; cursor:pointer was misleading and was removed. */}
          <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 12, padding: '1.25rem', transition: 'border-color .15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = B.purple}
            onMouseLeave={e => e.currentTarget.style.borderColor = B.border}>
            <div style={{ width: 48, height: 48, borderRadius: 10, background: B.purpleDeep, border: `0.5px solid ${B.purpleDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
                <rect x="4" y="3" width="20" height="22" rx="3" stroke={B.purple} strokeWidth="1.2"/>
                <line x1="9" y1="9" x2="19" y2="9" stroke={B.purple} strokeWidth="1.2" strokeLinecap="round"/>
                <line x1="9" y1="13" x2="19" y2="13" stroke={B.purple} strokeWidth="1.2" strokeLinecap="round"/>
                <line x1="9" y1="17" x2="15" y2="17" stroke={B.purple} strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
              <h3 style={{ fontSize: 15, fontWeight: 500, color: B.textPrimary, margin: 0 }}>My Reviews</h3>
              <span style={{ fontSize: 10, letterSpacing: '0.05em', padding: '2px 8px', borderRadius: 99, background: st.bg, color: st.color, border: `0.5px solid ${st.border}`, whiteSpace: 'nowrap', flexShrink: 0 }}>{st.label}</span>
            </div>
            <p style={{ fontSize: 12, color: B.textSecondary, lineHeight: 1.6, margin: '0 0 16px' }}>Access your annual and mid-year performance reviews, view manager feedback and track your rating history.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button onClick={() => navigate(reviewLink())} style={{ fontSize: 12, letterSpacing: '0.04em', padding: '7px 14px', borderRadius: 6, border: `0.5px solid ${B.purple}`, background: B.purpleDeep, color: B.purple, cursor: 'pointer', textAlign: 'left' }}>
                {myReview?.emp_status === 'submitted' ? 'View my submission →' : activeCycle ? `Start ${activeCycle.label} →` : 'View reviews →'}
              </button>
              <button onClick={() => navigate('/reviews/history')} style={{ fontSize: 12, letterSpacing: '0.04em', padding: '7px 14px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer', textAlign: 'left' }}>
                View review history
              </button>
            </div>
          </div>

          {/* Org Chart card — entire card is a single button (no nested
              interactive elements; outer card is keyboard-focusable). */}
          <button type="button" onClick={() => navigate('/org-chart')}
            style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 12, padding: '1.25rem', cursor: 'pointer', transition: 'border-color .15s', textAlign: 'left', font: 'inherit', color: 'inherit', display: 'block', width: '100%' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = B.purple}
            onMouseLeave={e => e.currentTarget.style.borderColor = B.border}>
            <div style={{ width: 48, height: 48, borderRadius: 10, background: B.purpleDeep, border: `0.5px solid ${B.purpleDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
                <rect x="11" y="3" width="6" height="6" rx="1" stroke={B.purple} strokeWidth="1.2"/>
                <rect x="3" y="19" width="6" height="6" rx="1" stroke={B.purple} strokeWidth="1.2"/>
                <rect x="11" y="19" width="6" height="6" rx="1" stroke={B.purple} strokeWidth="1.2"/>
                <rect x="19" y="19" width="6" height="6" rx="1" stroke={B.purple} strokeWidth="1.2"/>
                <path d="M14 9v5M6 19v-3h16v3" stroke={B.purple} strokeWidth="1.2" strokeLinecap="round" fill="none"/>
              </svg>
            </div>
            <h3 style={{ fontSize: 15, fontWeight: 500, color: B.textPrimary, margin: '0 0 8px' }}>Org Chart</h3>
            <p style={{ fontSize: 12, color: B.textSecondary, lineHeight: 1.6, margin: '0 0 16px' }}>Browse the company hierarchy. Search for anyone by name, title, or department; collapse levels to focus on a team.</p>
            <span style={{ display: 'inline-block', fontSize: 12, letterSpacing: '0.04em', padding: '7px 14px', borderRadius: 6, border: `0.5px solid ${B.purple}`, background: B.purpleDeep, color: B.purple, textAlign: 'left' }}>
              View org chart →
            </span>
          </button>

          {/* Company Docs card — entire card is a single button so it's
              keyboard-focusable; no nested interactive elements. */}
          <button type="button" onClick={() => navigate('/policies')}
            style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 12, padding: '1.25rem', cursor: 'pointer', transition: 'border-color .15s', textAlign: 'left', font: 'inherit', color: 'inherit', display: 'block', width: '100%' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = B.gold}
            onMouseLeave={e => e.currentTarget.style.borderColor = B.border}>
            <div style={{ width: 48, height: 48, borderRadius: 10, background: '#0f0d05', border: `0.5px solid ${B.goldDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
                <path d="M7 4h10l4 4v16H7z" stroke={B.gold} strokeWidth="1.2" strokeLinejoin="round"/>
                <path d="M17 4v4h4" stroke={B.gold} strokeWidth="1.2" strokeLinejoin="round"/>
                <line x1="11" y1="14" x2="18" y2="14" stroke={B.gold} strokeWidth="1.2" strokeLinecap="round"/>
                <line x1="11" y1="18" x2="18" y2="18" stroke={B.gold} strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            </div>
            <h3 style={{ fontSize: 15, fontWeight: 500, color: B.textPrimary, margin: '0 0 8px' }}>Company Docs</h3>
            <p style={{ fontSize: 12, color: B.textSecondary, lineHeight: 1.6, margin: '0 0 16px' }}>Browse Policies, Benefits, and Employee Resources. Click any document to open it in your browser.</p>
            <span style={{ display: 'inline-block', fontSize: 12, letterSpacing: '0.04em', padding: '7px 14px', borderRadius: 6, border: `0.5px solid ${B.goldDim}`, background: '#0f0d05', color: B.gold, textAlign: 'left' }}>
              View company docs →
            </span>
          </button>
        </div>

        {/* Announcements */}
        <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, marginBottom: 14, textTransform: 'uppercase' }}>Announcements</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {announcements.length === 0 && <p style={{ fontSize: 13, color: B.textMuted }}>No announcements at this time.</p>}
          {announcements.map(a => (
            <div key={a.id} style={{ background: B.surfaceLight, border: `0.5px solid ${a.pinned ? B.purpleDim : B.border}`, borderRadius: 10, padding: '14px 18px', display: 'flex', gap: 16 }}>
              <div style={{ width: 2, background: tagColor(a.tag), borderRadius: 99, alignSelf: 'stretch', flexShrink: 0, opacity: 0.7 }} />
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, letterSpacing: '0.06em', padding: '1px 8px', borderRadius: 99, color: tagColor(a.tag), border: `0.5px solid ${tagColor(a.tag)}`, opacity: 0.8 }}>{a.tag.toUpperCase()}</span>
                  {a.pinned && <span style={{ fontSize: 10, color: B.purple }}>📌 Pinned</span>}
                  <span style={{ fontSize: 11, color: B.textMuted }}>{new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
                <p style={{ fontSize: 13, fontWeight: 500, color: B.textPrimary, margin: '0 0 4px' }}>{a.title}</p>
                <p style={{ fontSize: 12, color: B.textSecondary, lineHeight: 1.6, margin: 0 }}>{a.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: `0.5px solid ${B.border}`, padding: '16px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <XIcon size={16} />
          <span style={{ fontSize: 11, color: B.textMuted, letterSpacing: '0.06em' }}>myhr.xlight.com</span>
        </div>
        <span style={{ fontSize: 11, color: B.textMuted }}>© 2026 xLight Inc. · Confidential</span>
      </div>
    </div>
  );
}
